/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-expressions */
import { getBreakpointMediaQuery } from 'js/constants.js';
import { fillTemplate } from '../common/filters/fill-template.js';
export { fillTemplate } from '../common/filters/fill-template.js';
export { handlePagination, updatePagination } from '../common/filters/pagination.js';

/**
 * Filter facets (used for search and URL params)
 * NOTE: these need the full field name, including suffix (e.g. "_ss")
 */
export const FilterFacets = Object.freeze({
    type: 'rl_programType_ss', // Full type (e.g. SI Silver)
    ptyp: 'rl_partner_type_s', // Type (e.g. SI, OEM)
    styp: 'rl_partner_subtype_ss', // Subtype (e.g. Silver, Is Var)
    dist: 'rl_distributor_level_ss', // Distributor Subtype (e.g. Is Var)
    capa: 'rl_disciplines_ss', // Capability
    indu: 'rl_industries_ss', // Industry
    prod: 'rl_prod_category_ss', // Product Category
    mach: 'rl_machine_seg_ss', // Machine Segment
    msub: 'rl_machine_subseg_ss', // Machine Sub-Segment
    cert: 'rl_certifications_ss', // Certification
    appl: 'rl_applications_ss', // Application
    plat: 'rl_prod_platforms_ss', // Platform
    conn: 'rl_prod_connectivities_ss', // Connectivity
});

export const OtherUrlParams = Object.freeze({
    accountId: 'id',
    productId: 'pid',
    isSalesOffice: 'so',
    aprCode: 'ac',
});

/**
 * API field name mapping
 * - Note: as long as these field values are used with the getter functions,
 *   they do not need Lucidworks suffixes
 * @param {string} prefix
 */
const generateLocationFields = (prefix) => ({
    Id: `${prefix}.Id`,
    Name: `${prefix}.Name`,
    AddressLine1: `${prefix}.AddressLine1`,
    AddressLine2: `${prefix}.AddressLine2`,
    AddressLine3: `${prefix}.AddressLine3`,
    City: `${prefix}.City`,
    StateProvince: `${prefix}.stateProvince`,
    StateProvince2: `${prefix}.StateProvince`, // For sales offices
    PostalCode: `${prefix}.PostalCode`,
    Country: `${prefix}.Country`,
    Country2: `${prefix}.country`, // For contacts
    Email: `${prefix}.Email`,
    Phone: `${prefix}.Phone`,
    Url: `${prefix}.url`,
    Latitude: `${prefix}.Latitude`,
    Longitude: `${prefix}.Longitude`,
    SalesOfficeBranchId: `${prefix}.SalesOfficeBranchId`,
    AprId: `${prefix}.APRId`,
    Regions: {
        IsAmericas: `${prefix}.IsAmericas`,
        IsLatinAmerica: `${prefix}.IsLatinAmerica`,
        IsEmea: `${prefix}.IsEmea`,
        IsAsia: `${prefix}.IsAsia`,
    }
});

export const Fields = Object.freeze({
    Id: 'id',
    Name: 'partner.mainPartnerInfos.Name',
    LogoId: 'partner.mainPartnerInfos.partnerLogoId',
    ProgramType: 'rl_programType', // e.g. SI Gold
    PartnerType: 'rl_partner_type', // e.g. SI
    PartnerSubtype: 'rl_partner_subtype', // e.g. Gold
    PrimaryLocation: generateLocationFields('partner.primaryLocation'),
    PrimaryLocationId: 'partner.mainPartnerInfos.PrimaryLocation',
    Url: 'partner.mainPartnerInfos.Url',
    FacebookUrl: 'partner.mainPartnerInfos.FacebookUrl',
    LinkedInUrl: 'partner.mainPartnerInfos.LinkedInUrl',
    TwitterUrl: 'partner.mainPartnerInfos.TwitterUrl',
    YouTubeUrl: 'partner.mainPartnerInfos.YouTubeUrl',
    Phone: 'partner.mainPartnerInfos.Phone',
    Description: 'partner.mainPartnerInfos.Description',
    Industries: 'partner.industries.Name',
    IndustryBadgeFlags: 'partner.industries.IndustriesShowBadgeOnLocator',
    MachineSegments: 'partner.machineSegments.Name',
    MachineSubSegments: 'partner.machineSubSegments.machineSubSegments_Name',
    Certifications: 'partner.certifications.Name', // Also contains Affiliations
    CertificationsUrl: 'partner.certifications.Url',
    CertificationsType: 'partner.certifications.Type', // 100000001 = Certification, 100000000 = Affiliation
    SuccessStories: 'partner.successStories.Description',
    SuccessStoriesUrl: 'partner.successStories.Url',
    Disciplines: 'partner.disciplines.Name',
    DisciplinesGroup: 'partner.disciplines.group',
    ProductCategories: 'partner.products.productcategoryName',
    YearEstablished: 'partner.mainPartnerInfos.YearEstablished',
    TotalEmployees: 'partner.mainPartnerInfos.TotalEmployees',
    TotalEngineers: 'partner.mainPartnerInfos.TotalEngineers',
    Distributor: {
        Name: 'partner.mainDistributorInfos.Name',
        AprCode: 'partner.mainDistributorInfos.AprCode',
        Subtype: 'partner.mainDistributorInfos.DistributorLevelText',
        LevelCode: 'partner.mainDistributorInfos.DistributorLevelCode',
        AprUrl: 'partner.mainDistributorInfos.AprUrl',
        Email: 'partner.mainDistributorInfos.Email',
        Phone: 'partner.mainDistributorInfos.MainPhone',
        LineCardUrl: 'partner.mainDistributorInfos.LineCardUrl',
        FacebookUrl: 'partner.mainDistributorInfos.FacebookUrl',
        LinkedInUrl: 'partner.mainDistributorInfos.LinkedInUrl',
        TwitterUrl: 'partner.mainDistributorInfos.TwitterUrl',
        YouTubeUrl: 'partner.mainDistributorInfos.YouTubeUrl',
        LocationId: 'partner.mainDistributorInfos.LocationId',
        LocationPhone: 'partner.mainDistributorInfos.LocationPhone',
        Location: generateLocationFields('partner.mainDistributorInfos'),
    },
    TechPartner: { // Technology Partner specific fields
        CompanyOverviewTitle: 'partner.mainPartnerInfos.CompanyOverviewTitle',
        ValuesStatementSection: 'partner.mainPartnerInfos.ValuesStatementSection',
        PortfolioOffering: 'partner.mainPartnerInfos.TechnologyPartnerPortfolioOffering',
        ValueToCustomer1: 'partner.mainPartnerInfos.ValueToCustomer1',
        ValueToCustomer2: 'partner.mainPartnerInfos.ValueToCustomer2',
        ValueToCustomer3: 'partner.mainPartnerInfos.ValueToCustomer3',
        KeyTakeaways1: 'partner.mainPartnerInfos.KeyTakeaways1',
        KeyTakeaways2: 'partner.mainPartnerInfos.KeyTakeaways2',
        KeyTakeaways3: 'partner.mainPartnerInfos.KeyTakeaways3',
        ShowImageOnLocator: 'partner.mainPartnerInfos.ShowImageOnLocator',
    },
    SalesOffice: {
        BranchId: 'partner.salesofficeInfos.BranchId',
        Name: 'partner.salesofficeInfos.Name',
        DisplayName: 'partner.salesofficeInfos.DisplayName',
        Email: 'partner.salesofficeInfos.Email',
        MainPhone: 'partner.salesofficeInfos.MainPhone',
        ServicePhone: 'partner.salesofficeInfos.ServiceDispatch',
        SupportPhone: 'partner.salesofficeInfos.LocalTechSupport',
        PartsPhone: 'partner.salesofficeInfos.PartsRepairExchange',
        Fax: 'partner.salesofficeInfos.FaxNumber',
        Location: generateLocationFields('partner.salesofficeInfos')
    },
    Products: {
        Id: 'partner.products.ProductId',
        Name: 'partner.products.ProductName',
        CatalogNumber: 'partner.products.CatalogNumber',
        PhotoId: 'partner.products.productPhoto.productId',
        Description: 'partner.products.ProductDescription',
        DescriptionTypo: 'partner.products.ProductDecription', // TEMP
        Overview: 'partner.products.ProductOverview',
        Category: 'partner.products.productcategoryName',
    },
    Contacts: {
        Id: 'partner.contacts.Id',
        FirstName: 'partner.contacts.FirstName',
        LastName: 'partner.contacts.LastName',
        Email: 'partner.contacts.Email',
        Phone: 'partner.contacts.Phone',
        Location: generateLocationFields('partner.contacts'),
        Regions: {
            IsAmericas: 'partner.contacts.Americas',
            IsLatinAmerica: 'partner.contacts.LatinAmerica',
            IsAsia: 'partner.contacts.Asia',
            IsEmea: 'partner.contacts.Emea',
        },
    },
    Locations: generateLocationFields('partner.locations'),
});

export const PartnerTypes = Object.freeze({
    Distributor: 'Authorized Distributor',
    OEM: 'OEM',
    SI: 'SI',
    TechnologyPartner: 'Technology Partner',
    EPCPartner: 'EPC Partner',
    SalesOffice: 'Sales Office',
    SoftwareReseller: 'Software Reseller',
});

// ==========
// CONFIG
// ==========
let sharedConfig;
/**
 * Get config values provided by LocatorConfigProvider.java
 * @returns {DOMStringMap?} */
export const getConfig = () => {
    if (!sharedConfig) {
        sharedConfig = document.querySelector('.locator-shared__config')?.dataset;
    }
    return sharedConfig;
};

/**
 * Lower-env override: on the email trigger element, keep raw for inspect and
 * put override on data-email so existing GCDC prefill picks it up. Raw is
 * never written into the GCDC form. Call at card/render time (not only on click).
 * @param {HTMLElement} el
 */
export const applyEmailOverrideToEmailElement = (el) => {
    const config = getConfig();
    const overrideAddress = (config?.emailOverrideAddress || '').trim();
    if (config?.emailOverrideEnabled !== 'true' || !overrideAddress) {
        return;
    }
    const currentEmail = (el?.dataset?.email || '').trim();
    if (!currentEmail) {
        return;
    }
    if (!el.dataset.originalNotificationEmailAddress) {
        if (currentEmail === overrideAddress) {
            return;
        }
        el.dataset.originalNotificationEmailAddress = currentEmail;
    }
    el.dataset.email = overrideAddress;
};

/** @param {ParentNode} [root=document] */
export const applyEmailOverrideToEmailElements = (root = document) => {
    root.querySelectorAll('[data-email]').forEach((el) => applyEmailOverrideToEmailElement(el));
};

const logoFolder = '/etc.clientlibs/rockwell-aem-base/clientlibs/clientlib-base/resources/logos';
export const LogoMap = Object.freeze({
    'Authorized Distributor': `${logoFolder}/authorized-distributor.svg`,
    'Authorized Distributor ': `${logoFolder}/authorized-distributor.svg`,
    'Authorized Distributor Specialty': `${logoFolder}/authorized-distributor.svg`,
    'Authorized Distributor Value-Add': `${logoFolder}/authorized-distributor.svg`,
    'Authorized Distributor Is Var': `${logoFolder}/authorized-distributor.svg`,
    'OEM Silver': `${logoFolder}/oem-partner-silver.svg`,
    'OEM Gold': `${logoFolder}/oem-partner-gold.svg`,
    'OEM Platinum': `${logoFolder}/oem-partner-platinum.svg`,
    'SI Silver': `${logoFolder}/system-integrator-silver.svg`,
    'SI Gold': `${logoFolder}/system-integrator-gold.svg`,
    'SI Platinum': `${logoFolder}/system-integrator-platinum.svg`,
    'Technology Partner': `${logoFolder}/technology-partner.svg`,
});

/** Map partner types/subtypes to display names */
export const partnerTypeDisplayNameMap = Object.freeze({
    SI: 'System Integrator',
    'Sales Office': 'Rockwell Automation Sales Office',
});

export const CertificationTypes = Object.freeze({
    Certification: 100000001,
    Affiliation: 100000000,
});

export const CapabilitiesGroups = Object.freeze({
    ProjectLifecycle: 'projectlifecyclecapability',
    Design: 'rockwelltechnologiesdesigncapability',
});

// ==========
// GET FIELDS
// ==========
/**
 * @param {Object} apiData
 * @param {string} fieldName
 * @returns {any[]}
 */
export const getFieldAsArray = (apiData, fieldName) => {
    // Solar adds suffixes to certain fields, we want to ignore those
    const apiSuffixes = ['', '_t', '_txt', '_s', '_ss', '_d', '_ds', '_i', '_is', '_b', '_bs'];
    // eslint-disable-next-line no-restricted-syntax
    for (const suffix of apiSuffixes) {
        let value = apiData[fieldName + suffix];
        if (value !== undefined) {
            if (!Array.isArray(value)) {
                value = [value];
            }
            return value.map((v) => (v === 'n/a' ? null : v));
        }
    }
    return [];
};

/**
 * @param {Object} apiData
 * @param {string} fieldName
 */
export const getField = (apiData, fieldName, arrayIndex = 0) => getFieldAsArray(apiData, fieldName)[arrayIndex];

/**
 * Address resolution, by partner type.
 *
 * Five field families feed this function. All are shaped by
 * `generateLocationFields`, but they come from different arrays and are not
 * equally complete:
 *
 *   Fields.Locations             partner.locations             full address
 *   Fields.Distributor.Location  partner.mainDistributorInfos  no City/State/Country
 *   Fields.PrimaryLocation       partner.primaryLocation       parent account HQ
 *   Fields.SalesOffice.Location  partner.salesofficeInfos      full address
 *   Fields.Contacts.Location     partner.contacts              full address
 *
 * Which family to read, and with which index:
 *
 *   Authorized Distributor   {@link getDistributorAddressString}, APR/OpCo index
 *   Sales Office             Fields.SalesOffice.Location, index 0
 *   Any other partner type   Fields.PrimaryLocation || Fields.Locations, index 0
 *   Locations list           Fields.Locations, indexed by that list itself
 *   Contact card             Fields.Contacts.Location, indexed by the contact
 *
 * Indices are not interchangeable across families. `partner.locations` and
 * `partner.mainDistributorInfos` are ordered independently and hold different
 * numbers of entries, so anything holding a distributor index must go through
 * {@link getDistributorLocationIndex} before it can read `partner.locations`.
 *
 * @param {Object} apiData
 * @param {Record<string, string>} address
 */
export const getAddressString = (
    apiData,
    {
        AddressLine1,
        AddressLine2,
        AddressLine3,
        City,
        StateProvince,
        StateProvince2,
        PostalCode,
        Country,
        Country2,
    },
    arrayIndex = 0,
    shortFormat = false
) => {
    const boundGetField = (fieldName) => getField(apiData, fieldName, arrayIndex);

    const addressLine1 = boundGetField(AddressLine1);
    const addressLine2 = boundGetField(AddressLine2);
    const addressLine3 = boundGetField(AddressLine3);
    const city = boundGetField(City);
    const stateProvince = boundGetField(StateProvince) ?? boundGetField(StateProvince2);
    const postalCode = boundGetField(PostalCode);
    const country = boundGetField(Country) ?? boundGetField(Country2);

    /**
     * Format helper
     * @param {string} string
     */
    const f = (string, suffix = '') => (string != null && string.trim() !== '' ? string + suffix : '');

    if (shortFormat) {
        return f(city, ', ') + f(stateProvince, ' ') + f(country);
    }
    return (
        f(addressLine1, '<br>')
        + f(addressLine2, '<br>')
        + f(addressLine3, '<br>')
        + f(city, ', ') + f(stateProvince, ' ') + f(postalCode) + ((city || stateProvince || postalCode) ? '<br>' : '')
        + f(country)
    );
};

/**
 * Index into `partner.locations` for a distributor branch, or `-1` when the
 * branch has no `locations` entry. `LocationId` → `Id` is the only thing
 * tying the two arrays together.
 *
 * Two flows produce `distributorIdx`, differing in where the APR comes from:
 *
 *   Partner search / distributor cards — APR is an input
 *
 *     zip ─▶ geoPoint ─▶ backend resolves the APR territory and echoes it
 *                        back in responseHeader.params.fq (rl_apr_code_ss)
 *                          │
 *                          ▼ getNearestOpCoIndex(doc, aprCode, location)
 *                            keep the mainDistributorInfos entries whose
 *                            AprCode matches, then the nearest by lat/lon
 *
 *   Suggested distributor, mismatch modal — APR is the output
 *
 *     zip ─▶ geocode ─▶ geoPoint ─▶ distributor search sorted by distance,
 *                                   numResults 1 ─▶ nearest partner doc
 *                          │
 *                          ▼ getNearestDistributorLocationIndex(doc, location)
 *                            nearest mainDistributorInfos entry, no APR
 *                            filter; the APR is then read off that entry
 *
 *   Both then share the same tail:
 *
 *     mainDistributorInfos[distributorIdx].LocationId ─▶ locations[].Id
 *                                                    ─▶ full address
 *
 * @param {Object} apiData
 * @param {number} distributorIdx Index into `partner.mainDistributorInfos`.
 */
export const getDistributorLocationIndex = (apiData, distributorIdx = 0) => {
    const locationId = getField(apiData, Fields.Distributor.LocationId, distributorIdx);
    return locationId == null
        ? -1
        : getFieldAsArray(apiData, Fields.Locations.Id).indexOf(locationId);
};

/**
 * Full address for a distributor branch, falling back
 * `locations[LocationId]` → `mainDistributorInfos[distributorIdx]` (street
 * lines only) → `primaryLocation` (parent account, often another city).
 * Tier 2 can't lead: it's non-empty but partial, so an `||` chain starting
 * there never reaches a complete address.
 *
 * @param {Object} apiData
 * @param {number} distributorIdx Index into `partner.mainDistributorInfos`.
 */
export const getDistributorAddressString = (apiData, distributorIdx = 0) => {
    const locationIdx = getDistributorLocationIndex(apiData, distributorIdx);
    return (locationIdx === -1 ? '' : getAddressString(apiData, Fields.Locations, locationIdx))
        || getAddressString(apiData, Fields.Distributor.Location, distributorIdx)
        || getAddressString(apiData, Fields.PrimaryLocation);
};

/**
 * Takes an array of region fields, evaluates their specific values within apiData,
 * and returns the display names of all regions fields that are `true`.
 * @param {Object} apiData
 * @param {Record<string, string>} regionFields
 */
export const getRegions = (apiData, regionFields, arrayIndex = 0) => {
    const regionDict = {
        IsAmericas: 'North America',
        IsLatinAmerica: 'Latin America',
        IsEmea: 'EMEA',
        IsAsia: 'Asia Pacific',
    };
    /** @type {string[]} */
    const regions = [];
    Object.entries(regionDict).forEach(([regionField, regionDisplayName]) => {
        if (getField(apiData, regionFields[regionField], arrayIndex)) {
            regions.push(regionDisplayName);
        }
    });
    return regions;
};

// ==========
// API CALL
// ==========
/** @param {Record<string, string | number>} searchParams */
export const fetchData = async (searchParams, options = {}) => {
    const apiUrl = getConfig().partnerSearchUrl;
    const headers = {
        client_id: getConfig().partnerSearchClientId,
        client_secret: getConfig().partnerSearchClientSecret,
        user_id: getConfig().partnerSearchUserId,
        user_email: getConfig().partnerSearchUserEmail,
        correlation_id: getConfig().partnerSearchCorrelationId,
    };
    const defaultSearchParams = {
        queryText: '',
        startIndex: 0,
        numResults: 1,
        role: 'comm_partners',
        segments: 'comm_partners',
        // collections: 'comm_partners',
        // spellingCorrect: true,
        // spellcheckPremium: 10,
        // languages: 'en',
    };

    const url = new URL(apiUrl);
    const allSearchParams = { ...defaultSearchParams, ...searchParams };
    Object.entries(allSearchParams)
        .filter(([, value]) => value != null)
        .forEach(([key, value]) => url.searchParams.append(key, value));

    const response = await fetch(url, { headers, ...options });
    if (!response.ok) {
        throw new Error(`Failed to fetch data from locator search API. Status: ${response.status} (${response.statusText}).`);
    }
    return response.json();
};

// ==========
// GET IMAGES
// ==========
/**
 * @param {URL} url
 * @param {RequestInit} options
 * @returns {Promise<string?>}
 */
const getImage = async (url, options) => {
    try {
        const response = await fetch(url, options);
        if (response.ok) {
            const base64Image = await response.text(); // API returns base64 encoded data string
            if (base64Image && base64Image.length > 64) {
                const dataUrl = `data:image/jpeg;base64,${base64Image}`;
                return dataUrl;
            }
        }
    } catch {
        // Error case handled below
    }
    return null;
};

/**
 * @param {string} url
 * @param {string} id
 */
const replaceRequestId = (url, id) => url
    .replaceAll('{id_with_dash}', id)
    .replaceAll('{id_without_dash}', id.replaceAll('-', ''));

/**
 * @param {string?} productPhotoId
 * @returns {Promise<string?>}
 */
export const getProductImage = async (productPhotoId) => {
    if (productPhotoId) {
        const url = new URL(replaceRequestId(getConfig().productPhotoUrl, productPhotoId));

        const headers = {
            'Content-Type': 'application/json',
            correlation_id: getConfig().productPhotoCorrelationId,
            client_id: getConfig().productPhotoClientId,
            client_secret: getConfig().productPhotoClientSecret,
        };

        return getImage(url, { headers });
    }
    return null;
};

/**
 * @param {string?} locationId
 * @param {string?} partnerId
 * @returns {Promise<string?>}
 */
export const getPartnerLogo = async (locationId, partnerId) => {
    if (locationId) {
        const logoFromProductEndpoint = await getProductImage(locationId);
        if (logoFromProductEndpoint) {
            return logoFromProductEndpoint;
        }
    }

    if (partnerId) {
        return `/bin/partner-logo?partnerId=${partnerId.replaceAll('-', '')}`;
    }
    return null;
};

/**
 * Attempt to load operating company logo from each location with a matching APR code,
 * then fall back to parent logo.
 * @param {{ apiData: Object, aprCode: string, parentLocationId?: string?, parentPartnerId?: string? }} params
 */
export const getOpCoLogo = async ({
    apiData, aprCode, parentLocationId = null, parentPartnerId = null
}) => {
    const aprLocationIds = getFieldAsArray(apiData, Fields.Distributor.AprCode)
        .map((locationApr, arrayIndex) => ({ locationApr, locationId: getField(apiData, Fields.Distributor.LocationId, arrayIndex) }))
        .filter(({ locationApr }) => locationApr === aprCode)
        .map(({ locationId }) => locationId);

    for (const locationId of aprLocationIds) { // eslint-disable-line no-restricted-syntax
        const opCoLogo = await getProductImage(locationId); // eslint-disable-line no-await-in-loop
        if (opCoLogo) {
            return opCoLogo;
        }
    }

    return getPartnerLogo(parentLocationId, parentPartnerId);
};

export const getPortalImage = async (partnerId) => {
    const url = new URL(replaceRequestId(getConfig().partnerLogoUrl, partnerId).replace('/logos', '/images/portal'));
    const headers = {
        'Content-Type': 'application/json',
        correlation_id: getConfig().partnerLogoCorrelationId,
        client_id: getConfig().partnerLogoClientId,
        client_secret: getConfig().partnerLogoClientSecret,
    };
    return getImage(url, { headers });
};

// ==========
// DISTRIBUTOR MISMATCH MODAL
// ==========
// When a user clicks the email button on a distributor card and then enters
// an address into the gated content form whose APR (Authorized Partner Region)
// differs from the selected distributor, we want to surface a confirmation
// modal so they can choose to either proceed with the originally selected
// distributor or switch to the recommended one for their address.
//
// The modal element itself is rendered from the
// `distributorMismatchDialog` HTL template in `locator-templates.html`.

const MISMATCH_DIALOG_SELECTOR = '#distributor-mismatch-dialog__dialog';

// Last radio the user checked, so reopening the modal shows their pick
// instead of snapping back to the recommendation. Keyed by the
// selected+recommended APR pair — a different pair is a different
// question, so it starts fresh.
const lastDistributorChoice = new Map();

/**
 * Open the distributor mismatch confirmation modal and resolve with the
 * user's choice.
 *
 * @param {{
 *   selectedDistributor: { name: string, address: string },
 *   recommendedDistributor: { name: string, address: string },
 *   choiceKey?: string
 * }} params
 * @returns {Promise<'selected' | 'recommended' | 'cancel'>} The action the
 *   user took. `'cancel'` is returned when the dialog is dismissed via the
 *   Back button, the X close button, or the Escape key.
 */
export const showDistributorMismatchModal = ({
    selectedDistributor, recommendedDistributor, choiceKey,
}) => {
    const dialog = document.querySelector(MISMATCH_DIALOG_SELECTOR);
    if (!dialog) {
        // Template was not included on this page — fall back to "cancel" so
        // the calling code can decide how to proceed.
        return Promise.resolve('cancel');
    }

    // Populate dynamic content
    const form = dialog.querySelector('form');
    fillTemplate(form, 'data-x', {
        selectedName: selectedDistributor.name || '',
        selectedAddress: selectedDistributor.address || '',
        recommendedName: recommendedDistributor.name || '',
        recommendedAddress: recommendedDistributor.address || '',
    }, { keepIfEmpty: true });

    // Render the i18n description with the recommended name bolded in place
    // of the `{0}` placeholder.
    const descriptionEl = dialog.querySelector('.distributor-mismatch-dialog__description');
    if (descriptionEl) {
        const template = descriptionEl.dataset.template || '';
        descriptionEl.replaceChildren();
        const parts = template.split('{0}');
        descriptionEl.append(document.createTextNode(parts[0] ?? ''));
        const strong = document.createElement('strong');
        strong.textContent = recommendedDistributor.name || '';
        descriptionEl.append(strong);
        descriptionEl.append(document.createTextNode(parts[1] ?? ''));
    }

    // Restore the last choice for this pairing; nothing stored (first open)
    // falls through to the recommended option.
    const storedChoice = choiceKey ? lastDistributorChoice.get(choiceKey) : null;
    const radioToCheck = (storedChoice
        && form.querySelector(`input[name="distributor-choice"][value="${storedChoice}"]`))
        || form.querySelector('input[name="distributor-choice"][value="recommended"]');
    if (radioToCheck) {
        radioToCheck.checked = true;
    }

    return new Promise((resolve) => {
        const onClose = () => {
            dialog.removeEventListener('close', onClose);
            // returnValue is set by the submit button's `value` attribute
            // when the form is submitted with method="dialog".
            const action = dialog.returnValue;
            if (action !== 'confirm') {
                resolve('cancel');
                return;
            }
            const choice = form.querySelector('input[name="distributor-choice"]:checked')?.value ?? 'recommended';
            if (choiceKey) {
                lastDistributorChoice.set(choiceKey, choice);
            }
            resolve(choice === 'selected' ? 'selected' : 'recommended');
        };
        dialog.addEventListener('close', onClose);
        // Reset the previous returnValue so a fresh dismiss doesn't leak.
        dialog.returnValue = '';
        if (typeof dialog.showModal === 'function') {
            dialog.showModal();
        } else {
            dialog.show();
        }
    });
};

// ==========
// APR LOOKUP
// ==========
/**
 * Geocode a zip / postal code to latitude / longitude via the Azure Maps
 * address search API. Returns `null` when the zip is empty, the request
 * fails, or no result is found.
 *
 * The country is appended to the query string when available so an
 * ambiguous postal code (e.g. "11201" is valid in both Brooklyn, NY and
 * Cádiz, Spain) resolves to the country the user actually entered on the
 * form.
 *
 * @param {string?} zipCode
 * @param {string?} [country]
 * @returns {Promise<{ lat: number, lon: number } | null>}
 */
const geocodeZipCode = async (zipCode, country) => {
    const trimmedZip = zipCode == null ? '' : String(zipCode).trim();
    if (trimmedZip === '') {
        return null;
    }

    const bingMapsKey = getConfig()?.bingMapsKey;
    if (!bingMapsKey) {
        return null;
    }

    const trimmedCountry = country == null ? '' : String(country).trim();
    const query = trimmedCountry ? `${trimmedZip}, ${trimmedCountry}` : trimmedZip;

    const apiUrl = new URL('https://atlas.microsoft.com/search/address/json');
    apiUrl.searchParams.set('subscription-key', bingMapsKey);
    apiUrl.searchParams.set('api-version', '1.0');
    apiUrl.searchParams.set('query', query);
    apiUrl.searchParams.set('limit', '1');

    try {
        const response = await fetch(apiUrl);
        if (!response.ok) {
            return null;
        }
        const json = await response.json();
        const position = json?.results?.[0]?.position;
        if (position?.lat == null || position?.lon == null) {
            return null;
        }
        return { lat: position.lat, lon: position.lon };
    } catch {
        return null;
    }
};

/**
 * Look up the closest Authorized Distributor for a given zip / postal code
 * and decide whether it matches the user's currently selected distributor.
 *
 * The check works as follows:
 *   1. Geocode the zip code to lat/lon via Azure Maps.
 *   2. Ask the partner search API for the nearest Authorized Distributor
 *      doc to those coordinates (the API is geo-sorted via `sort=dist`).
 *   3. Within that doc, pick the nearest operating-company location to the
 *      user's coordinates and read its APR code directly off the document.
 *   4. Compare with the selected distributor's APR code.
 *
 * Reading the APR straight from the matched location keeps this logic
 * independent of how the underlying search index represents resolved APR
 * regions in its query metadata.
 *
 * Returns `null` when the zip can't be geocoded, when no distributor is
 * returned, or when the closest distributor matches the selected one
 * (i.e. nothing to confirm).
 *
 * @param {{ zipCode?: string?, country?: string?, selectedAprCode?: string? }} params
 * @returns {Promise<{ name: string, email: string?, address: string, aprCode: string } | null>}
 */
// Run the partner-search API call with a single retry on transient
// failures. The QA/dev backend periodically returns 500 for the
// distributor lookup and succeeds on retry; without any retry the
// mismatch modal would skip on those flaky responses.
//
// Crucially this is *one* retry, not two — each partner-search call
// takes 3-5 seconds on the QA backend, so a second retry would add
// another 4-9 seconds of dead time before the modal opens (or the
// fall-through submit fires) which the user perceives as the modal
// "taking forever". A single 100ms-delayed retry catches the common
// transient case while keeping the worst case under ~10 seconds.
const fetchPartnerSearchWithRetry = async (params, { retries = 1, retryDelayMs = 100 } = {}) => {
    let lastErr;
    for (let attempt = 0; attempt <= retries; attempt += 1) { // eslint-disable-line no-restricted-syntax
        try {
            return await fetchData(params); // eslint-disable-line no-await-in-loop
        } catch (err) {
            lastErr = err;
            if (attempt === retries) {
                break;
            }
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => { setTimeout(resolve, retryDelayMs * (attempt + 1)); });
        }
    }
    throw lastErr;
};

// Cache of in-flight + completed nearest-distributor lookups, keyed by
// `zip|country`. The partner-search call costs 3-5 seconds against the
// real backend, so caching both the resolved value AND the in-flight
// Promise lets us:
//   * skip the round-trip entirely on a re-submit (e.g. after the user
//     dismisses the modal, tweaks a field and submits again with the
//     same zip+country)
//   * share a single in-flight lookup between a background prefetch
//     and the on-submit check (instead of firing two parallel 4-second
//     requests, the on-submit await falls into the already-running
//     prefetch promise and resolves the moment the prefetch does).
const nearestDistributorCache = new Map();
const nearestDistributorCacheKey = (zip, country) => `${(zip || '').trim().toLowerCase()}|${(country || '').trim().toLowerCase()}`;

// Lookup pipeline shared by `lookupRecommendedDistributor` and the
// idle-time prefetch. APR comparison happens in the caller so the cache
// is reusable across multiple selected distributors (the same zip
// always resolves to the same nearest distributor regardless of which
// card the user clicked).
const lookupNearestAuthorizedDistributor = async (zipCode, country) => {
    const position = await geocodeZipCode(zipCode, country);
    if (!position) {
        return null;
    }

    let data;
    try {
        data = await fetchPartnerSearchWithRetry({
            facets: `${FilterFacets.ptyp}:"${PartnerTypes.Distributor}"`,
            geoPoint: `${position.lat},${position.lon}`,
            startIndex: 0,
            numResults: 1,
            sort: 'dist',
        });
    } catch {
        return null;
    }

    const doc = data?.response?.docs?.[0];
    if (!doc) {
        return null;
    }

    // Find the nearest operating-company branch on the matched distributor
    // and read its APR off the doc. This avoids relying on the search
    // backend echoing a resolved APR filter back in the response metadata.
    const userLocation = { latitude: position.lat, longitude: position.lon };
    // eslint-disable-next-line no-use-before-define
    const nearestIdx = getNearestDistributorLocationIndex(doc, userLocation);
    const opCoIdx = nearestIdx >= 0 ? nearestIdx : 0;

    const aprCode = getField(doc, Fields.Distributor.AprCode, opCoIdx);
    if (!aprCode) {
        return null;
    }

    return {
        name: getField(doc, Fields.Distributor.Name, opCoIdx)
            ?? getField(doc, Fields.Name),
        email: getField(doc, Fields.Distributor.Email, opCoIdx)
            ?? getField(doc, Fields.PrimaryLocation.Email),
        address: getDistributorAddressString(doc, opCoIdx),
        aprCode,
    };
};

// Get-or-fetch wrapper. Returns the cached Promise if a lookup for this
// zip+country is already running or completed; otherwise starts a fresh
// lookup and caches the Promise so concurrent callers share it.
//
// On lookup failure (null result) we evict the entry so the next call
// can retry — caching `null` indefinitely would mask a transient
// backend error for the whole session.
const getNearestDistributorPromise = (zipCode, country) => {
    const trimmedZip = (zipCode || '').trim();
    if (!trimmedZip) {
        return Promise.resolve(null);
    }
    const key = nearestDistributorCacheKey(trimmedZip, country);
    if (nearestDistributorCache.has(key)) {
        return nearestDistributorCache.get(key);
    }
    const promise = lookupNearestAuthorizedDistributor(trimmedZip, (country || '').trim())
        .then((result) => {
            if (result == null) {
                nearestDistributorCache.delete(key);
            }
            return result;
        })
        .catch((err) => {
            nearestDistributorCache.delete(key);
            throw err;
        });
    nearestDistributorCache.set(key, promise);
    return promise;
};

export const lookupRecommendedDistributor = async ({ zipCode, country, selectedAprCode }) => {
    const recommended = await getNearestDistributorPromise(zipCode, country);
    if (!recommended) {
        return null;
    }
    if (selectedAprCode && recommended.aprCode === selectedAprCode) {
        return null;
    }
    return recommended;
};

// Per-gate registry of distributor info to apply on the next form submit.
// Keyed by gateId (without `gate-` prefix). Populated by
// `attachDistributorMismatchInterceptor`, consumed by the document-level
// submit listener below.
const distributorMismatchRegistry = new Map();

// Read the value of a named input on a GCDC form, falling back across a
// list of candidate names so we tolerate variant field names across forms.
const readFormInputByName = (formEl, candidateNames) => {
    for (const name of candidateNames) { // eslint-disable-line no-restricted-syntax
        const el = formEl.querySelector(`[name="${name}"]`);
        const value = el?.value;
        if (value != null && String(value).trim() !== '') {
            return String(value).trim();
        }
    }
    return null;
};

// Write a value onto the first matching named input on a GCDC form and
// fire input/change events so any GCDC field-bindings pick it up.
const writeFormInputByName = (formEl, candidateNames, value) => {
    for (const name of candidateNames) { // eslint-disable-line no-restricted-syntax
        const el = formEl.querySelector(`[name="${name}"]`);
        if (el) {
            el.value = value;
            el.dispatchEvent(new Event('input', { bubbles: true }));
            el.dispatchEvent(new Event('change', { bubbles: true }));
            return;
        }
    }
};

// Forms we're about to re-submit after the user confirms in the modal.
// Tracked via a `WeakSet` rather than a dataset flag so the bypass works
// across both the click and submit listeners in the same re-submit cycle
// without one consuming it before the other sees it. Cleared on a
// microtask after the synchronous event cycle drains.
const formsBypassingMismatchCheck = new WeakSet();

// Forms with an in-flight mismatch check (async APR lookup + open modal).
// Used to drop subsequent submit attempts the user makes while the
// previous attempt's modal is still pending — otherwise rapid double
// clicks (or a click that lands between the lookup finishing and the
// modal animating in) trigger parallel `showModal()` calls which throw
// `InvalidStateError` because <dialog> can't be opened twice.
const formsWithMismatchCheckInFlight = new WeakSet();

let answeredMismatchDestination = null;

// Re-trigger the user's original submit gesture by clicking the submit
// button. We click the button (rather than calling `form.requestSubmit()`)
// because GCDC submits via XHR from its own click handler on the
// `.gcdc-form-button`; calling `form.submit()` or `form.requestSubmit()`
// alone would bypass GCDC's submission path entirely.
const triggerOriginalSubmit = (formEl) => {
    formsBypassingMismatchCheck.add(formEl);

    const submitBtn = formEl.querySelector(
        'input[type="submit"].gcdc-form-button, input[type="submit"], button[type="submit"]',
    );
    if (submitBtn) {
        submitBtn.click();
    } else if (typeof formEl.requestSubmit === 'function') {
        formEl.requestSubmit();
    } else {
        formEl.submit();
    }

    // Clear after the click/submit event cycle has fully drained so the
    // next user-initiated submit triggers the check again.
    Promise.resolve().then(() => formsBypassingMismatchCheck.delete(formEl));
};

// Shared check used by both the click and submit listeners. Returns
// synchronously after halting the source event (so the caller can rely on
// preventDefault being applied before any await), then proceeds with the
// async APR lookup and modal flow.
const handleMismatchCheck = async ({ formEl, sourceEvent }) => {
    // Bypass any re-entrant click/submit triggered by `triggerOriginalSubmit`.
    if (formsBypassingMismatchCheck.has(formEl)) {
        return;
    }

    // Only intercept genuine user-initiated submit gestures. GCDC's
    // "we've saved you some time" recognition flow programmatically
    // submits the form when the visitor's previous submission is
    // recognised — those events have `isTrusted === false`. Surfacing
    // the mismatch modal in that path is jarring because the user
    // hasn't actually pressed Submit yet, so we let the auto-submit
    // through and only show the modal when the user clicks the
    // submit button themselves (or hits Enter in a field).
    if (sourceEvent.isTrusted === false) {
        return;
    }

    // GCDC form ids look like `gate-<uuid>_form_<timestamp>`. Pull the gate
    // id back out so we can look up which distributor (if any) the user
    // was on when the modal was opened.
    const match = formEl.id.match(/^gate-(.+?)_form_/);
    if (!match) {
        return;
    }
    const selectedDistributor = distributorMismatchRegistry.get(match[1]);
    if (!selectedDistributor) {
        return;
    }

    // Current Partner Locator form uses `$Postcode`; tolerated aliases
    // cover any other form variants that may be wired up later.
    // eslint-disable-next-line no-use-before-define
    const zipCode = readFormInputByName(formEl, ZIP_FIELD_CANDIDATES);
    if (!zipCode) {
        return;
    }
    // eslint-disable-next-line no-use-before-define
    const country = readFormInputByName(formEl, COUNTRY_FIELD_CANDIDATES);

    // Returns before the `preventDefault` below, so GCDC submits untouched.
    const destinationKey = `${selectedDistributor.aprCode ?? ''}|${zipCode}|${country ?? ''}`
        .trim().toLowerCase();
    if (answeredMismatchDestination === destinationKey) return;

    // Halt the source event BEFORE awaiting so GCDC's own click/submit
    // handlers (which submit via XHR) don't run in parallel with the modal.
    sourceEvent.preventDefault();
    sourceEvent.stopImmediatePropagation();

    // Single-flight: if a previous submit's check is still in flight (or
    // its modal is still on screen) drop this one. The source event is
    // already halted above, so the form won't submit either way.
    if (formsWithMismatchCheckInFlight.has(formEl)) {
        return;
    }
    formsWithMismatchCheckInFlight.add(formEl);

    try {
        let recommended = null;
        try {
            recommended = await lookupRecommendedDistributor({
                zipCode, country, selectedAprCode: selectedDistributor.aprCode,
            });
        } catch (err) {
            // Lookup is best-effort — fall through to the original submit.
            console.warn('Distributor APR lookup failed:', err); // eslint-disable-line no-console
        }

        // No mismatch (or lookup unavailable) — re-fire the original submit.
        if (!recommended) {
            triggerOriginalSubmit(formEl);
            return;
        }

        const choice = await showDistributorMismatchModal({
            selectedDistributor: {
                name: selectedDistributor.name,
                address: selectedDistributor.address,
            },
            recommendedDistributor: {
                name: recommended.name,
                address: recommended.address,
            },
            choiceKey: `${selectedDistributor.aprCode ?? ''}|${recommended.aprCode ?? ''}`,
        });

        if (choice === 'cancel') {
            // Keep the gated form open with the user's input intact.
            return;
        }

        answeredMismatchDestination = destinationKey;

        // Route the submission to whichever distributor was confirmed. Written
        // for both radios, so confirming the selected one also undoes the
        // fields an earlier "recommended" confirm wrote.
        const chosen = choice === 'recommended' ? recommended : selectedDistributor;
        const config = getConfig();
        const overrideAddress = (config?.emailOverrideAddress || '').trim();
        const useOverride = config?.emailOverrideEnabled === 'true' && !!overrideAddress;
        writeFormInputByName(
            formEl,
            ['$NotificationEmailAddress'],
            useOverride ? overrideAddress : chosen.email,
        );
        writeFormInputByName(formEl, ['$locatorPartnerName'], chosen.name);
        /* eslint-disable no-use-before-define */
        gatedFormPartnerName = chosen.name;
        applyGatedFormTitle(match[1], chosen.name);
        /* eslint-enable no-use-before-define */

        triggerOriginalSubmit(formEl);
    } finally {
        formsWithMismatchCheckInFlight.delete(formEl);
    }
};

const ZIP_FIELD_CANDIDATES = ['$Postcode', '$Zipcode', '$ZipCode', '$PostalCode', '$ZipPostalCode'];
const COUNTRY_FIELD_CANDIDATES = ['$Country'];

// Per-submit-button record of the most recent click event we observed
// on it (timestamp + isTrusted). The browser fires a `submit` event
// with `isTrusted=true` regardless of whether the click that produced
// it was a real user gesture or a programmatic `submitBtn.click()` —
// AND `form.requestSubmit()` fires `submit` with `isTrusted=true`
// without firing any preceding click at all. So to tell apart a true
// user submit from one of GCDC's internal re-fires we cross-reference
// the submit event's `submitter` against this map. Real user gestures
// (mouse click on Submit, or Enter-key implicit submission, which the
// HTML spec defines as firing a click on the default submit button)
// always leave a trusted click here within the same event-loop tick;
// programmatic `requestSubmit()` leaves nothing.
const lastClickOnSubmitter = new WeakMap();

// Install single document-level capture-phase `click` and `submit`
// listeners the first time anyone arms the interceptor.
//
// We need document-level rather than per-form because GCDC asynchronously
// creates and replaces its form element AFTER `gcdcModalOpened` fires — a
// listener bound to a specific form instance gets orphaned when GCDC
// swaps the form out.
//
// We need BOTH click and submit because GCDC submits via XHR from its own
// click handler on the `.gcdc-form-button` submit button. A submit-only
// listener stops the native form POST but GCDC's XHR has already gone
// out by then. Intercepting the click in capture phase runs BEFORE
// GCDC's click handler, so `stopImmediatePropagation()` prevents it
// from firing. The submit listener remains as a fallback for keyboard
// submits (Enter in a text input) which don't go through a button click.
//
// The same listener block also installs an idle-time prefetch on the
// zip / country fields so the partner-search round-trip (3-5s on the
// real backend) is *already in flight* by the time the user clicks
// submit. In the common case the on-submit await falls into a Promise
// that's already resolved and the modal opens essentially instantly.
let documentSubmitListenerInstalled = false;
const ensureDocumentSubmitListener = () => {
    if (documentSubmitListenerInstalled) {
        return;
    }
    documentSubmitListenerInstalled = true;

    document.addEventListener('click', (clickEvent) => {
        const btn = clickEvent.target?.closest?.(
            'input[type="submit"].gcdc-form-button, input[type="submit"], button[type="submit"]',
        );
        if (!btn) {
            return;
        }
        const formEl = btn.closest('form.gcdc-form-render');
        if (!formEl) {
            return;
        }
        // Record this click so the synchronously-following submit event
        // can read its trustedness back out (see submit listener below).
        lastClickOnSubmitter.set(btn, {
            isTrusted: clickEvent.isTrusted,
            t: performance.now(),
        });
        handleMismatchCheck({ formEl, sourceEvent: clickEvent });
    }, { capture: true });

    document.addEventListener('submit', (submitEvent) => {
        const formEl = submitEvent.target;
        if (!(formEl instanceof HTMLFormElement)) {
            return;
        }
        if (!formEl.classList.contains('gcdc-form-render')) {
            return;
        }
        // Browser-generated submit events are always `isTrusted=true`
        // regardless of whether the originating click was programmatic
        // (and `form.requestSubmit()` fires one with no preceding click
        // at all). Cross-reference `event.submitter` against the click
        // we just recorded above:
        //   * recent (<50ms) trusted click on the submitter → real user
        //   * recent untrusted click on the submitter → `.click()` from
        //     code (GCDC recognition flow / our own re-submit / etc.)
        //   * no recent click on the submitter → `requestSubmit()` /
        //     dispatched submit event → also programmatic
        // Anything that isn't a real user gesture is dropped so the
        // mismatch modal doesn't surface for GCDC's own re-submits.
        const { submitter } = submitEvent;
        const recordedClick = submitter ? lastClickOnSubmitter.get(submitter) : null;
        const isUserSubmit = !!recordedClick
            && recordedClick.isTrusted
            && performance.now() - recordedClick.t < 50;
        // Consume the recorded click so a follow-up `requestSubmit()`
        // can't re-use the same user click as cover.
        if (submitter && recordedClick) {
            lastClickOnSubmitter.delete(submitter);
        }
        if (!isUserSubmit) {
            return;
        }
        handleMismatchCheck({ formEl, sourceEvent: submitEvent });
    }, { capture: true });

    // Idle-time prefetch: every time the user changes the zip or country
    // field, debounce 400ms then start the nearest-distributor lookup if
    // we have a usable zip. The result lands in `nearestDistributorCache`
    // and the on-submit `handleMismatchCheck` picks it up for free.
    //
    // The debounce only resets on changes to *zip/country* fields, not
    // on every keystroke in the form, so typing in unrelated fields
    // doesn't delay the prefetch. Per-form last-key tracking prevents
    // re-firing when the value hasn't actually changed (avoids
    // duplicate fetches on `change` after `input`, etc.).
    let prefetchTimer = null;
    const prefetchLastKey = new WeakMap();
    document.addEventListener('input', (ev) => {
        const { target } = ev;
        if (!(target instanceof HTMLElement) || !target.matches('input, select')) {
            return;
        }
        const name = target.getAttribute('name');
        if (!ZIP_FIELD_CANDIDATES.includes(name) && !COUNTRY_FIELD_CANDIDATES.includes(name)) {
            return;
        }
        const formEl = target.closest('form.gcdc-form-render');
        if (!formEl) {
            return;
        }
        // Only check forms that have a distributor registered — no point
        // prefetching for non-distributor gated forms.
        const match = formEl.id.match(/^gate-(.+?)_form_/);
        if (!match || !distributorMismatchRegistry.has(match[1])) {
            return;
        }
        clearTimeout(prefetchTimer);
        prefetchTimer = setTimeout(() => {
            const zipCode = readFormInputByName(formEl, ZIP_FIELD_CANDIDATES);
            if (!zipCode) {
                return;
            }
            const country = readFormInputByName(formEl, COUNTRY_FIELD_CANDIDATES);
            const key = nearestDistributorCacheKey(zipCode, country);
            if (prefetchLastKey.get(formEl) === key) {
                return;
            }
            prefetchLastKey.set(formEl, key);
            // Fire-and-forget: the resulting Promise (or its cached
            // result) is picked up by `handleMismatchCheck` later.
            // `handleMismatchCheck` will surface failures via its own
            // try/catch path so we can safely swallow here.
            getNearestDistributorPromise(zipCode, country).catch(() => {});
        }, 400);
    }, { capture: true });
};

/**
 * Arm the distributor-mismatch check for the next submit of the given
 * gate. The actual interception happens in the document-level submit
 * listener installed by `ensureDocumentSubmitListener` so we don't have to
 * race GCDC's asynchronous form rendering.
 *
 * @param {{
 *   gateId: string,
 *   selectedDistributor: { name: string, email: string, aprCode: string, address: string }
 * }} params
 */
const attachDistributorMismatchInterceptor = ({ gateId, selectedDistributor }) => {
    if (!gateId) {
        return;
    }
    ensureDocumentSubmitListener();
    distributorMismatchRegistry.set(gateId, selectedDistributor);
};

// Module-level state for the currently-open gated form. Each call to
// `openGatedForm` resets these so a new open never picks up stale
// handlers / timers / pre-fill state from a previous open.
//
// We previously stacked one `gcdcModalOpened` handler + one
// `gcdcGateReady` handler + one polling interval per open without
// ever tearing them down. After a few open/close cycles every
// subsequent open fired every stacked handler in order, each one
// re-arming the mismatch interceptor for a stale distributor and
// re-running pre-fill. The last write won so the values ended up
// correct, but the redundant writes plus the redundant interceptor
// arming were the "wonky" modal behaviour.
//
// Single-trigger pre-fill (driven only by `formReadyPollInterval`
// below) also fixes the autofill double-override the user reported:
// each `performLocationPreFill` call was running multiple field
// writes via internal `setTimeout` retries AND being invoked from
// three different triggers (`gcdcModalOpened` + polling +
// `gcdcGateReady`), so the same field could be overwritten three or
// more times within ~800ms. If the user typed in that window, our
// next write reverted their change. Now: one trigger, one write per
// field, guarded by `hasPerformedLocationPreFill`.
let activeGcdcModalOpenedHandler = null;
let formReadyPollInterval = null;
let formReadyPollTimeout = null;
let hasPerformedLocationPreFill = false;
let gatedFormPartnerName = null;

// Matched on structure, not id: GCDC ids the heading `gcdc-modal-intro-<rand>`
// until its aria pass runs and `gcdc-modal-aria-title-<rand>` after, so an
// id-based selector misses whenever the modal is opened mid-load.
const GCDC_FORM_TITLE_SELECTOR = '.gcdc-form-intro .h3';

/**
 * Retitle the gated form to name the partner being contacted.
 *
 * @param {string} gateId Gate id without the `gate-` prefix.
 * @param {string} partnerName
 */
const applyGatedFormTitle = (gateId, partnerName) => {
    const template = getConfig()?.gatedFormTitleTemplate;
    if (!template || !partnerName) return;

    // Scoped to our gate so another gate's modal isn't retitled.
    const formEl = document.querySelector(`form[id^="gate-${gateId}_form_"]`);
    const titleEl = formEl?.querySelector(GCDC_FORM_TITLE_SELECTOR);
    const title = template.replace('{0}', partnerName);
    if (titleEl && titleEl.textContent !== title) {
        titleEl.textContent = title;
    }
};

/**
 * Gated content form handler
 * - Runs when an email button is pressed
 * - Reads email from button data attribute and fills it into form
 * @param {MouseEvent} ev
 */
export const openGatedForm = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    const targetEl = ev.currentTarget;
    applyEmailOverrideToEmailElement(targetEl);
    const {
        email, name, aprCode, partnerType, address
    } = targetEl.dataset;
    const { gatedForm } = getConfig();
    const gateIdWithoutHash = gatedForm.slice(6); // Remove #gate-

    // Tear down handlers/timers left behind by any previous open so the
    // new open is the only one driving pre-fill and interceptor arming.
    if (activeGcdcModalOpenedHandler) {
        window.removeEventListener('gcdcModalOpened', activeGcdcModalOpenedHandler);
        activeGcdcModalOpenedHandler = null;
    }
    if (formReadyPollInterval != null) {
        clearInterval(formReadyPollInterval);
        formReadyPollInterval = null;
    }
    if (formReadyPollTimeout != null) {
        clearTimeout(formReadyPollTimeout);
        formReadyPollTimeout = null;
    }
    // Reset for this open. Set true the moment pre-fill writes successfully
    // so any further attempt (the still-running poll iteration, a delayed
    // GCDC ready event, etc.) bails out without overwriting user input.
    hasPerformedLocationPreFill = false;
    gatedFormPartnerName = name;
    answeredMismatchDestination = null;
    lastDistributorChoice.clear();

    // Armed here rather than from `gcdcModalOpened`, which on a fast click
    // fires before the gate reaches `GateInstances` and leaves the check
    // unarmed. Deleted first so a non-distributor open can't inherit it.
    distributorMismatchRegistry.delete(gateIdWithoutHash);
    if (partnerType === PartnerTypes.Distributor && aprCode) {
        attachDistributorMismatchInterceptor({
            gateId: gateIdWithoutHash,
            selectedDistributor: {
                name: name || '', email: email || '', aprCode, address: address || ''
            },
        });
    }

    // Run pre-fill exactly once per gated-form open. Returns true once
    // it's actually written to the form, signalling the poller to stop.
    const performLocationPreFill = () => {
        if (hasPerformedLocationPreFill) return true;

        // Prefer location from a `locator-search` ancestor when the gated
        // form is opened from the partner-locator search page (that
        // element holds the user's currently-selected search location).
        // On pages that don't have it (PDP, partner-detail without a
        // search wrapper, …) fall back to the same localStorage sources
        // the rest of the locator code already writes to, so pre-fill
        // behaves consistently wherever the gated form is opened from.
        let locationToUse = null;
        const locatorSearch = targetEl.closest('locator-search');
        if (locatorSearch) {
            const currentLocation = locatorSearch.state.location;
            const currentUserLocation = locatorSearch.state.userLocation;
            if (currentLocation && (currentLocation.country || currentLocation.countrySubdivisionName || currentLocation.municipality)) {
                locationToUse = currentLocation;
            } else if (currentUserLocation && (currentUserLocation.country || currentUserLocation.countrySubdivisionName || currentUserLocation.municipality)) {
                locationToUse = currentUserLocation;
            }
        } else {
            // Pure localStorage reads (no network) so the poll stays
            // synchronous and the 10s safety cap still applies.
            try {
                const loggedInRaw = window.localStorage.getItem('loggedInData');
                const loggedInData = loggedInRaw ? JSON.parse(loggedInRaw) : null;
                if (loggedInData && loggedInData.country && (loggedInData.city || loggedInData.region)) {
                    locationToUse = {
                        country: loggedInData.country,
                        countrySubdivisionName: loggedInData.region,
                        municipality: loggedInData.city,
                    };
                } else {
                    // eslint-disable-next-line no-use-before-define
                    const storedRaw = window.localStorage.getItem(LOCATION_STORAGE_KEY);
                    const stored = storedRaw ? JSON.parse(storedRaw) : null;
                    if (stored && (stored.country || stored.countrySubdivisionName || stored.municipality)) {
                        locationToUse = stored;
                    }
                }
            } catch {
                // Malformed JSON in localStorage — fall through; user will
                // just have to fill the location fields manually.
            }
        }
        if (!locationToUse) return false;

        // Country requires both the input AND its autocomplete listbox to
        // exist. If either is missing we let the poller try again.
        const countryInput = document.querySelector('input[name="$Country"]');
        const countryAutocomplete = document.querySelector('[id*="Country"][id*="listbox"]');
        if (!countryInput || !countryAutocomplete) return false;

        // Latch BEFORE the writes so a concurrent poll iteration started
        // in between the `if` above and the writes below can't also fire.
        hasPerformedLocationPreFill = true;

        if (window.ff && typeof window.ff.disableCountryDetection === 'function') {
            window.ff.disableCountryDetection();
        }

        const { country } = locationToUse;
        const city = locationToUse.municipality;
        const state = locationToUse.countrySubdivisionName;

        if (country) {
            countryInput.focus();
            countryInput.value = country;
            ['input', 'keyup', 'focus', 'change'].forEach((eventType) => {
                countryInput.dispatchEvent(new Event(eventType, { bubbles: true }));
            });
            // Autocomplete options populate after the input event, so wait
            // a tick and then click the matching option — but bail out if
            // the user has already typed something different in the
            // meantime so we don't snap their input back.
            setTimeout(() => {
                if (countryInput.value !== country) return;
                const countryOption = Array.from(countryAutocomplete.querySelectorAll('a[role="option"]'))
                    .find((option) => option.textContent.toLowerCase().trim() === country.toLowerCase().trim());
                if (countryOption) {
                    countryInput.setAttribute('aria-activedescendant', countryOption.id);
                    countryOption.setAttribute('aria-selected', 'true');
                    countryOption.click();
                }
            }, 100);
        }

        if (city) {
            // City depends on the country selection finishing, hence the
            // small delay. Skip the write if the user has typed something
            // else by then.
            setTimeout(() => {
                const cityInput = document.querySelector('input[name="$City"]');
                if (!cityInput || cityInput.value) return;
                cityInput.focus();
                cityInput.value = city;
                ['input', 'keyup', 'focus', 'change', 'blur'].forEach((eventType) => {
                    cityInput.dispatchEvent(new Event(eventType, { bubbles: true }));
                });
            }, 200);
        }

        if (state) {
            // State select only renders for countries that have one (US,
            // CA, …) so wait for the country selection to flip the form
            // and surface it. Skip if the user has already picked a
            // value themselves.
            setTimeout(() => {
                const stateSelect = document.querySelector('select[name="$StateorProvince"]');
                if (!stateSelect || stateSelect.value || stateSelect.closest('.hidden')) return;
                const matchingOption = Array.from(stateSelect.options).find((option) => (
                    option.text.toLowerCase().trim() === state.toLowerCase().trim()
                    || option.value.toLowerCase() === state.toLowerCase().trim()
                    || (state.length === 2 && option.value === state.toUpperCase())
                ));
                if (matchingOption) {
                    stateSelect.value = matchingOption.value;
                    stateSelect.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }, 400);
        }

        return true;
    };

    activeGcdcModalOpenedHandler = (modalOpenEvent) => {
        // Fill the static (non-location) fields — these are GCDC API
        // calls that are idempotent and don't overwrite user input.
        const gcdcObj = modalOpenEvent.detail;
        const locatorGate = gcdcObj.GateInstances.find((gateInstance) => gateInstance.id === gateIdWithoutHash);
        if (!locatorGate) return;

        if (email) {
            locatorGate.form.getFieldByName('$NotificationEmailAddress').setValue(email, true);
        }
        if (name) {
            locatorGate.form.getFieldByName('$locatorPartnerName').setValue(name, true);
        }
        const locatorSearch = targetEl.closest('locator-search');
        if (locatorSearch) {
            const locationInfo = `${locatorSearch.elements.locationSelectText.textContent};${locatorSearch.readLocationState()}`;
            locatorGate.form.getFieldByName('$locatorLocation').setValue(locationInfo, true);
        }
    };
    window.addEventListener('gcdcModalOpened', activeGcdcModalOpenedHandler, { once: false });

    // Polled because GCDC renders the form asynchronously after the click.
    // Runs the full 10s window; pre-fill is a no-op once latched.
    formReadyPollInterval = setInterval(() => {
        performLocationPreFill();
        applyGatedFormTitle(gateIdWithoutHash, gatedFormPartnerName);
    }, 150);
    formReadyPollTimeout = setTimeout(() => {
        if (formReadyPollInterval != null) {
            clearInterval(formReadyPollInterval);
            formReadyPollInterval = null;
        }
        formReadyPollTimeout = null;
    }, 10000);

    document.querySelector('#locator-shared__gated-form-link').click();
    targetEl.dispatchEvent(new CustomEvent('locator__email-clicked', { detail: targetEl.dataset, bubbles: true }));
};

// ==========
// HELPER FUNCTIONS AND TEMPLATES
// ==========
// [LEGACY BREAKPOINT: max-width 48em -> getBreakpointMediaQuery('down', 'small')]
export const getMatchMobile = () => getBreakpointMediaQuery('down', 'small');

/** @param {MouseEvent} ev */
export const openContactDialog = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    const targetEl = ev.currentTarget;
    const { title, phone } = targetEl.dataset;
    const contactDialog = document.querySelector('#contact-dialog__dialog');
    fillTemplate(contactDialog, 'data-xt', {
        title,
        phone,
        telLink: `tel:${phone}`
    }, { keepIfEmpty: true });
    contactDialog.show();
    targetEl.dispatchEvent(new CustomEvent('locator__phone-clicked', { detail: targetEl.dataset, bubbles: true }));
};

// ==========
// LOCATION HELPERS
// ==========
/**
 * The Microsoft.Maps.Search module only works if you have a map on the page,
 * which is why the REST endpoint is used here instead.
 *
 * @param {number} latitude
 * @param {number} longitude
 * @returns {Promise<string>}
 */
const getLocationName = async (latitude, longitude) => {
    if (latitude == null || longitude == null) {
        return '';
    }
    const apiUrl = new URL('https://atlas.microsoft.com/search/address/reverse/json');
    apiUrl.searchParams.set('subscription-key', getConfig().bingMapsKey);
    apiUrl.searchParams.set('api-version', '1.0');
    apiUrl.searchParams.set('query', `${latitude},${longitude}`);
    const response = await fetch(apiUrl);
    const json = await response.json();

    const address = json?.addresses?.[0]?.address;
    const displayAddress = [
        address?.municipality,
        address?.countrySubdivisionName || address?.countrySubdivisionCode,
        address?.country
    ];
    return displayAddress.filter((part) => part != null).join(', ');
};

/*
 * Where the locator's geo point comes from — one chain for every entry point,
 * via `readEffectiveLocation`. Each tier calls Azure Maps only for the half it
 * doesn't already hold:
 *
 *   ① readSearchLocation()      localStorage['ra_locator_search_location']
 *      [no Azure] picked in the locator UI, stored with its coordinates
 *        │ no latitude/longitude
 *        ▼
 *   ② readLoggedInLocation()    localStorage['loggedInData']
 *      [Azure forward geocode, every attempt] a postal address with no
 *      coordinates; needs address + city + region + country
 *        │ a field is missing, or no position resolved
 *        ▼
 *   ③ readUserLocation()        window.getPiraObj()
 *      [Azure reverse geocode, only for `getName`] coordinates but no place
 *      name; polls up to 4s, so callers holding a result pass `profileLocation`
 *        │ no profile, or no coordinates on it
 *        ▼
 *      coordinates stay null and callers degrade rather than guess — otherwise
 *      getNearestOpCoIndex falls back to opCoIdxs[0], an arbitrary branch.
 *
 * The GCDC form pre-fill (`performLocationPreFill`) does NOT use this chain: it
 * reads component state on the search page, `loggedInData` → search location
 * elsewhere, and never the profile.
 */
const LOCATION_STORAGE_KEY = 'ra_locator_search_location';

/**
 * Geocode a display address, or `null` when no position can be resolved —
 * missing key, transport error, error status and empty result set all collapse
 * to `null` so callers have a single "no coordinates" case to fall back on.
 * @returns {Promise<{ lat: number, lon: number }?>}
 */
const getLocationCoordinates = async (locationName) => {
    try {
        const apiUrl = new URL('https://atlas.microsoft.com/search/address/json');
        apiUrl.searchParams.set('subscription-key', getConfig()?.bingMapsKey);
        apiUrl.searchParams.set('api-version', '1.0');
        apiUrl.searchParams.set('query', locationName);
        const response = await fetch(apiUrl);
        if (!response.ok) {
            return null;
        }
        const json = await response.json();
        const position = json?.results?.[0]?.position;
        return position?.lat != null && position?.lon != null ? position : null;
    } catch {
        return null;
    }
};

/** The location the visitor picked in the locator UI. @see writeStoredLocation */
const readSearchLocation = () => {
    const storedLocation = JSON.parse(window.localStorage.getItem(LOCATION_STORAGE_KEY));
    return {
        name: storedLocation?.name,
        latitude: storedLocation?.latitude,
        longitude: storedLocation?.longitude,
        country: storedLocation?.country,
        countrySubdivisionName: storedLocation?.countrySubdivisionName,
        municipality: storedLocation?.municipality
    };
};

/**
 * The signed-in account's address, geocoded. `null` when the account address is
 * incomplete or won't geocode — a name without coordinates is useless here,
 * since every caller needs a geo point to rank branches by distance.
 */
const readLoggedInLocation = async () => {
    const loggedInData = JSON.parse(window.localStorage.getItem('loggedInData'));
    if (!loggedInData?.address || !loggedInData.city || !loggedInData.region || !loggedInData.country) {
        return null;
    }
    const loggedInDataAddress = [
        loggedInData.city,
        loggedInData.region,
        loggedInData.country
    ];
    const coordinates = await getLocationCoordinates(loggedInDataAddress);
    if (coordinates == null) {
        return null;
    }
    return {
        name: loggedInDataAddress.join(', '),
        latitude: coordinates.lat,
        longitude: coordinates.lon,
        country: loggedInData.country,
        countrySubdivisionName: loggedInData.region,
        municipality: loggedInData.city,
    };
};

/**
 * The location every entry point resolves distributors against. Tiers and
 * fallback conditions are diagrammed above `LOCATION_STORAGE_KEY`.
 *
 * Shared by the search page, partner detail and the PDP card. Each used to
 * apply its own order, so the same distributor resolved to a different branch
 * depending on which page the visitor arrived from.
 *
 * @param {boolean?} getName Reverse-geocode a display name in tier 3.
 * @param {object?} profileLocation Tier 3 when the caller already resolved it,
 * so pages holding a `readUserLocation` result don't reverse-geocode twice.
 * @returns {Promise<{ name: string?, latitude: number?, longitude: number?, country: string?, countrySubdivisionName: string?, municipality: string? }>}
 */
export const readEffectiveLocation = async (getName = false, profileLocation = null) => {
    const searchLocation = readSearchLocation();
    if (searchLocation.latitude != null && searchLocation.longitude != null) {
        return searchLocation;
    }
    const loggedInLocation = await readLoggedInLocation();
    if (loggedInLocation != null) {
        return loggedInLocation;
    }
    // eslint-disable-next-line no-use-before-define
    return profileLocation ?? readUserLocation(getName);
};

/** @param {{ name: string?, latitude: number?, longitude: number?, country: string?, countrySubdivisionName: string?, municipality: string? }} location */
export const writeStoredLocation = (location) => {
    window.localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(location));
};

/**
 * @param {boolean?} getName
 * @returns {Promise<{ name: string?, latitude: number?, longitude: number?, country: string?, countrySubdivisionName: string?, municipality: string? }>}
 */
export const readUserLocation = async (getName = false) => {
    // Wait for getPiraObj to be available with timeout
    // eslint-disable-next-line no-use-before-define
    const piraObj = await waitForPiraObj();

    let name = null;
    let municipality = null;
    let countrySubdivisionName = null;
    let country = null;

    if (getName && piraObj?.latitude != null && piraObj?.longitude != null) {
        name = await getLocationName(piraObj.latitude, piraObj.longitude);

        // Parse the name string (format: "municipality, countrySubdivisionName, country")
        if (name) {
            const parts = name.split(',').map((part) => part.trim()).filter((part) => part !== '');
            municipality = parts[0] || null;
            countrySubdivisionName = parts[1] || null;
            country = parts[2] || null;
        }
    }

    return {
        name,
        latitude: piraObj?.latitude,
        longitude: piraObj?.longitude,
        country,
        countrySubdivisionName,
        municipality,
    };
};

/**
 * Wait for getPiraObj to be available before proceeding
 * @param {number} timeout - Maximum time to wait in milliseconds (default: 4000ms)
 * @returns {Promise<Object?>} Resolves with piraObj when getPiraObj is available, null on timeout
 */
const waitForPiraObj = async (timeout = 4000) => new Promise((resolve) => {
    const startTime = Date.now();

    const checkPiraObj = () => {
        if (typeof window.getPiraObj === 'function') {
            resolve(window.getPiraObj());
        } else if (Date.now() - startTime > timeout) {
            console.warn('getPiraObj not available after timeout, returning null'); // eslint-disable-line no-console
            resolve(null);
        } else {
            // Check again in 1000ms
            setTimeout(checkPiraObj, 1000);
        }
    };
    checkPiraObj();
});

/**
 * Haversine formula
 * @param {{ latitude: number?, longitude: number? }} location1
 * @param {{ latitude: number?, longitude: number? }} location2
 * @returns {number}
 */
export const calcGeoDistance = (location1, location2) => {
    if (location1.latitude === location2.latitude && location1.longitude === location2.longitude) {
        return 0;
    }

    const radLat1 = (Math.PI * location1.latitude) / 180;
    const radLat2 = (Math.PI * location2.latitude) / 180;
    const theta = location1.longitude - location2.longitude;
    const radTheta = (Math.PI * theta) / 180;

    let dist = Math.sin(radLat1) * Math.sin(radLat2)
        + Math.cos(radLat1) * Math.cos(radLat2) * Math.cos(radTheta);

    if (dist > 1) {
        dist = 1;
    }

    dist = ((Math.acos(dist) * 180) / Math.PI) * 60 * 1.1515;
    return dist;
};

/**
 * @param {Object} apiData
 * @param {string} aprCode
 * @param {{ latitude?: number, longitude?: number }?} userLocation - User location
 * @returns {number} The index of the nearest location with matching APR
 */
export const getNearestOpCoIndex = (apiData, aprCode, userLocation) => {

    // Get indexes of all locations that match the APR code
    const opCoIdxs = getFieldAsArray(apiData, Fields.Distributor.AprCode)
        .map((distAprCode, index) => (distAprCode === aprCode ? index : -1))
        .filter((index) => index !== -1);

    // If a user location is given, find the closest out of the matching locations
    if (userLocation?.latitude && userLocation?.longitude) {
        const latitudeArr = getFieldAsArray(apiData, Fields.Distributor.Location.Latitude);
        const longitudeArr = getFieldAsArray(apiData, Fields.Distributor.Location.Longitude);

        let closestIdx = -1;
        let closestDistance = Infinity;

        opCoIdxs.forEach((index) => {
            const thisLocation = { latitude: latitudeArr[index], longitude: longitudeArr[index] };
            const distance = calcGeoDistance(userLocation, thisLocation);
            if (distance < closestDistance) {
                closestIdx = index;
                closestDistance = distance;
            }
        });

        return closestIdx;
    }

    // If no location is given, fallback to the first matching location
    return opCoIdxs[0];
};

/**
 * Like {@link getNearestOpCoIndex}, but does not filter on APR code.
 * Returns the index of the distributor location within `apiData` whose
 * coordinates are closest to `userLocation`.
 *
 * @param {Object} apiData
 * @param {{ latitude?: number, longitude?: number }} userLocation
 * @returns {number} The index of the nearest distributor location, or `-1`
 *   when the doc has no usable coordinates.
 */
export const getNearestDistributorLocationIndex = (apiData, userLocation) => {
    if (!userLocation?.latitude || !userLocation?.longitude) {
        return -1;
    }
    const latitudeArr = getFieldAsArray(apiData, Fields.Distributor.Location.Latitude);
    const longitudeArr = getFieldAsArray(apiData, Fields.Distributor.Location.Longitude);
    const count = Math.max(latitudeArr.length, longitudeArr.length);

    let closestIdx = -1;
    let closestDistance = Infinity;
    for (let i = 0; i < count; i += 1) {
        const lat = latitudeArr[i];
        const lon = longitudeArr[i];
        if (lat == null || lon == null) {
            continue; // eslint-disable-line no-continue
        }
        const distance = calcGeoDistance(userLocation, { latitude: lat, longitude: lon });
        if (distance < closestDistance) {
            closestIdx = i;
            closestDistance = distance;
        }
    }
    return closestIdx;
};

export const backLinkSessionStorageKey = 'filteredPageUrl';
